import { Box, Title, Modal, LeadsResponse, Lead, TableProps, Card, capitalizeFirstLetter } from 'vopay';

import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useFetchLeadsPartnerId from '@/hooks/useApi/useFetchLeadsPartnerId';
import { usePartnerLogin } from '@/providers/PartnerLogin.provider';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import LeadsCredentialPanel from './LeadsCredentialPanel';
import { Button, TransactionStatus, Table } from 'UI';
import { useTranslation } from 'react-i18next';

const LeadsList = (): React.JSX.Element => {
  const history = useHistory();
  const [partnerLeads, getPartnerLeads] = useFetchLeadsPartnerId({});
  const { canSeeAccountCredential } = useModuleFeatureFlag('accounts');
  const [viewCredentials, setViewViewCredential] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Leads.List' });

  const [currentToken, setCurrentToken] = useState('');
  const [leads, setLeads] = useState<LeadsResponse>();
  const {
    response: { accountId, isRoleViewOnly },
  } = usePartnerLogin();
  const viewCredentialsClose = () => {
    setViewViewCredential(false);
  };
  useEffect(() => {
    getPartnerLeads(accountId);
  }, []);
  useEffect(() => {
    setLeads(partnerLeads);
  }, [partnerLeads]);

  const tableProps: TableProps<Lead> = {
    id: 'leads-list-table',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'leadName',
        header: t('leadName'),
        align: 'left',
        template: ({ data }) => (
          <Table.Link to={{ pathname: `leads/${data.HubspotLeadID}` }}>{data.Company}</Table.Link>
        ),
      },
      {
        id: 'status',
        header: t('status'),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus status="neutral" label={capitalizeFirstLetter(data.LeadStatus) || ''} />
        ),
      },
      {
        id: 'referredBy',
        header: t('referredBy'),
        align: 'left',
        field: 'ReferredBy',
      },
      {
        id: 'dateAdded',
        header: t('dateAdded'),
        align: 'left',
        field: 'DateAdded',
      },
      {
        id: 'dateUpdated',
        header: t('dateUpdated'),
        align: 'left',
        field: 'LastModified',
      },
      {
        id: 'ViewCredentials',
        header: t('viewCredentials'),
        align: 'left',
        template: ({ data }) =>
          canSeeAccountCredential && data.Token ? (
            <Button
              size="small"
              id={`lead-view-credental-${data.Company}`}
              disabled={isRoleViewOnly}
              onClick={() => {
                setCurrentToken(data.Token!);
                setViewViewCredential(true);
              }}
            >
              {t('viewCredentialsButton')}
            </Button>
          ) : (
            ''
          ),
      },
    ],
    rows: Object.values(leads?.Leads || {}).map((lead) => ({
      id: String(lead.HubspotLeadID || lead.Company),
      data: lead,
    })),
  };

  return (
    <Box flx="col" gapSize="36px">
      <Box jc="sb" ai="center">
        <Title title={t('pageTitle')} />
        <Button
          id="leads-addLeads"
          colorScheme="primary"
          disabled={isRoleViewOnly}
          onClick={() =>
            history.push({
              pathname: '/leads/create',
              search: `?from=${encodeURIComponent(history.location.pathname)}`,
            })
          }
        >
          {t('createLeadButton')}
        </Button>
      </Box>
      <Card
        style={{
          padding: '16px',
        }}
      >
        <Table {...tableProps} />
      </Card>

      <Modal isVisible={viewCredentials} onBgClick={viewCredentialsClose} enableBgClick>
        <Box maxwidth="800px">
          <LeadsCredentialPanel token={currentToken} />
        </Box>
      </Modal>
    </Box>
  );
};

export default LeadsList;
